/* RESPONSIBLE TEAM: team-actions */
import type AppService from 'embercom/services/app-service';
import SettingsRoute from 'embercom/routes/apps/app/settings/base/base-settings-route';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { hash } from 'rsvp';
import { task } from 'ember-concurrency-decorators';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'embercom/services/intl';
import type Transition from '@ember/routing/transition';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { get } from 'embercom/lib/ajax';
import { ReactRoute } from 'embercom/lib/react-route';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

const REQUIRED_PERMISSION = 'can_access_developer_hub';
/** Event identifier for dirty state changes - must match React side */
const DIRTY_STATE_EVENT = 'data-connector-config:dirty-state-changed';

export class CustomActionsCustomAction extends SettingsRoute {
  @service declare store: $TSFixMe;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare intercomConfirmService: $TSFixMe;
  @service declare appService: AppService;
  @service declare permissionsService: $TSFixMe;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  // Track dirty state from React via event bridge
  private hasUnsavedChanges = false;
  private unsubscribeDirtyState?: () => void;

  reactEnabled() {
    return this.appService.app.canUseFeature('data-connectors-react-config-page');
  }

  activate() {
    // Subscribe to dirty state events from React when in React mode
    if (this.reactEnabled()) {
      this.unsubscribeDirtyState = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
        DIRTY_STATE_EVENT,
        (data) => {
          let { isDirty } = data as { isDirty: boolean };
          this.hasUnsavedChanges = isDirty;
        },
      );
    }
  }

  deactivate() {
    this.unsubscribeDirtyState?.();
    this.hasUnsavedChanges = false;
  }

  @tracked filters = {
    success: 'any',
    error_type: 'any',
    created_at: 168,
    id: 'any',
    conversation_id: 'any',
  };

  analytics = {
    section: 'settings',
    place: 'action-builder',
  };

  get titleToken() {
    return this.intl.t('new-settings.app-settings.custom-actions.action-builder-title');
  }

  async beforeModel(transition: Transition) {
    if (!this.permissionsService.currentAdminCan(REQUIRED_PERMISSION)) {
      transition.abort();
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(REQUIRED_PERMISSION);
      return;
    }

    let action = this.store.peekRecord('workflow-connector/action', transition.to.params.id);

    if (action) {
      action.unloadRecord();
    }
  }

  async model(params: $TSFixMe) {
    // If React is enabled, skip Ember model loading (React will handle it)
    if (this.reactEnabled()) {
      return null;
    }

    let [ticketTypes, objectTypes, conversationAtributes] = await Promise.all([
      this.store.findAll('inbox/ticket-type'),
      this.store.findAll('custom-objects/object-type'),
      // conversation-attributes/descriptor are loaded so they can be used by attribute-service
      this.store.findAll('conversation-attributes/descriptor'),
      this.store.findAll('workflow-connector/mcp-server'),
    ]);

    this.intercomEventService.trackAnalyticsEvent({
      action: 'viewed',
      object: 'settings',
      action_id: params.id,
    });
    return await hash({
      action: this.store.find('workflow-connector/action', params.id),
      objectTypes,
      conversationAtributes,
      actionLogs: [],
      ticketTypes,
      customAuthenticationTokens: this.store.query('custom-authentication/token', {
        with_app_package_tokens: true,
      }),
      loadNextPage: this.loadNextPage,
      updateFilters: this.updateFilters,
    });
  }

  @task({ restartable: true })
  *loadInitialLogs(): TaskGenerator<void> {
    let actionId = (this.controller.model as $TSFixMe).action.id;
    let actionLogParams = {
      page: 1,
      app_id: this.appService.app.id,
      action_id: actionId,
    } as $TSFixMe;

    if (this.filters) {
      actionLogParams.success = this.filters.success;
      actionLogParams.error_type = this.filters.error_type;
      actionLogParams.created_at = this.filters.created_at;
      if (this.filters.id !== 'any') {
        actionLogParams.id = this.filters.id;
      }
      if (this.filters.conversation_id !== 'any') {
        actionLogParams.conversation_id = this.filters.conversation_id;
      }
    }

    let response = yield get('/ember/workflow_connector/action_execution_results', actionLogParams);

    this.store.pushPayload({
      'workflow-connector/action-execution-result': response.action_execution_results,
    });

    let actionLogs = (this.controller.model as $TSFixMe).actionLogs;
    actionLogs.set('meta', response.meta);
    actionLogs.clear();
    actionLogs.pushObjects(
      response.action_execution_results.map((aer: $TSFixMe) =>
        this.store.peekRecord('workflow-connector/action-execution-result', aer.id),
      ),
    );
  }

  @task({ restartable: true })
  *loadNextPage(): TaskGenerator<void> {
    let actionId = (this.controller.model as $TSFixMe).action.id;
    let actionLogs = (this.controller.model as $TSFixMe).actionLogs;
    let currentPage = actionLogs.meta ? actionLogs.meta.page : 0;
    let nextPage = actionLogs.meta ? actionLogs.meta.next_page : 1;
    if (nextPage > currentPage) {
      let actionLogParams = {
        page: nextPage,
        app_id: this.appService.app.id,
        action_id: actionId,
      } as $TSFixMe;
      if (this.filters) {
        actionLogParams.success = this.filters.success;
        actionLogParams.error_type = this.filters.error_type;
        actionLogParams.created_at = this.filters.created_at;
        if (this.filters.id !== 'any') {
          actionLogParams.id = this.filters.id;
        }
        if (this.filters.conversation_id !== 'any') {
          actionLogParams.conversation_id = this.filters.conversation_id;
        }
      }

      let response = yield get(
        '/ember/workflow_connector/action_execution_results',
        actionLogParams,
      );

      this.store.pushPayload({
        'workflow-connector/action-execution-result': response.action_execution_results,
      });

      actionLogs.set('meta', response.meta);
      actionLogs.pushObjects(
        response.action_execution_results.map((aer: $TSFixMe) =>
          this.store.peekRecord('workflow-connector/action-execution-result', aer.id),
        ),
      );
    }
  }

  setupController(_controller: $TSFixMe, _model: $TSFixMe) {
    super.setupController(...arguments);

    // Check if actionExecutionResultId is in query params and apply it to filters
    if (_controller.actionExecutionResultId) {
      this.filters.id = _controller.actionExecutionResultId.toString();
    }

    taskFor(this.loadInitialLogs).perform();
  }

  @action
  updateFilters(filters = {}) {
    (this.controller.model as $TSFixMe).actionLogs.set('meta', null);
    (this.controller.model as $TSFixMe).actionLogs.clear();
    this.filters = filters as $TSFixMe;
    taskFor(this.loadInitialLogs).perform();
  }

  @action
  async willTransition(transition: Transition) {
    let confirmOptions = {
      primaryButtonType: 'primary-destructive',
      title: this.intl.t('workflow-connector.builder.modal.title'),
      confirmButtonText: this.intl.t('workflow-connector.builder.modal.confirm-button'),
      cancelButtonText: this.intl.t('workflow-connector.builder.modal.cancel-button'),
      body: this.intl.t('workflow-connector.builder.modal.body'),
    };

    // Check dirty state from appropriate source based on React/Ember mode
    let hasDirtyAttributes: boolean;
    if (this.reactEnabled()) {
      // React mode: dirty state is tracked via event subscription from React
      hasDirtyAttributes = this.hasUnsavedChanges;
    } else {
      // Ember mode: dirty state is tracked via Ember Data model
      hasDirtyAttributes = (this.controller.model as $TSFixMe).action.hasDirtyAttributes;
    }

    if (hasDirtyAttributes) {
      transition.abort();
      let isConfirmed = await this.intercomConfirmService.confirm(confirmOptions);
      if (isConfirmed) {
        if (!this.reactEnabled()) {
          (this.controller.model as $TSFixMe).action.rollbackAllAttributes();
        }
        // Clear local dirty state before retrying transition
        this.hasUnsavedChanges = false;
        transition.retry();
      }
    }
  }
}

export default ReactRoute(CustomActionsCustomAction);
